import { gql } from '@graphql/__generated__';
import { Leads_Appraisal_Reason_Enum } from '@graphql/__generated__/graphql';

export const BUY_APPRAISAL_REASONS = [
  Leads_Appraisal_Reason_Enum.BuyThis,
  Leads_Appraisal_Reason_Enum.BuySoon,
] as const;

export const GET_USERS_LIST = gql(/* GraphQL */ `
  query GetUsersList(
    $where: users_bool_exp!
    $order_by: [users_order_by!]!
    $limit: Int!
    $offset: Int!
  ) {
    users(where: $where, order_by: $order_by, limit: $limit, offset: $offset) {
      id
      created_at
      first_name
      last_name
      onboarding_status
      is_admin
      is_broker
      account_manager_id
      account_manager {
        id
        full_name
      }
      user_images(where: { is_primary: { _eq: true } }, limit: 1) {
        id
        image {
          url
        }
      }
      emails {
        primary
        email
      }
      phone_numbers {
        primary
        number
      }
    }
  }
`);

export const BULK_REASSIGN_USER_ACCOUNT_MANAGER = gql(/* GraphQL */ `
  mutation BulkReassignUserAccountManager(
    $users: jsonb!
    $newAccountManagerId: uuid!
    $transferOpenActivities: Boolean!
  ) {
    bulk_reassign_user_account_manager(
      args: {
        selected_users: $users
        new_account_manager_id: $newAccountManagerId
        transfer_open_activities: $transferOpenActivities
      }
    ) {
      id
      account_manager_id
      account_manager {
        id
        full_name
      }
    }
  }
`);

export const GET_USERS_LIST_COUNT = gql(/* GraphQL */ `
  query GetUsersListCount($where: users_bool_exp!, $limit: Int) {
    users_aggregate(where: $where, limit: $limit) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_USER = gql(/* GraphQL */ `
  query GetUser($id: uuid!, $includeSubscriptions: Boolean!) {
    users_by_pk(id: $id) {
      id
      is_deleted
      onboarding_status
      has_done_onboarding
      membership_type
      full_name
      language
      ...UserActiveModuleGrants
      referrer
      referring_domain
      landing_page_url
      created_at
      created_by
      home_address_route
      home_address_street_number
      home_address_postcode
      home_address_locality
      home_address_state
      home_address_country_code
      home_address_lat
      home_address_lng
      ...UserSettingsFragment
      ...ContactCardUserFragment
      user_images(where: { is_primary: { _eq: true } }, limit: 1) {
        image {
          id
          url
        }
      }
      default_team_id
      teams_users(order_by: [{ deactivated: asc }, { team: { name: asc } }]) {
        is_leader
        deactivated
        team {
          id
          name
        }
      }
      default_team {
        id
        name
      }
      broker {
        ...UserCard_user
      }
      account_manager {
        ...UserCard_user
      }
      auto_assign_leads
      # Subscriptions are matched to the user by email (customer_email +
      # tenant_id), not by a user_id FK -- legacy rows often have no user_id,
      # so the email relationship is the only way to surface past/expired subs.
      subscription_emails: emails @include(if: $includeSubscriptions) {
        subscriptions(order_by: { created: desc }) {
          id
          total_amount
          status
          created
          current_period_start
          current_period_end
          items
          customer
          plan_name
        }
      }
    }
  }
`);

export const USER_SETTINGS_FRAGMENT = gql(/* GraphQL */ `
  fragment UserSettingsFragment on users {
    id
    first_name
    last_name
    is_broker
    is_admin
    has_free_crm_access
    can_edit_settings
    can_user_login_as
    agent_slug
    show_in_agency_pages
  }
`);

export const UPDATE_USER_SETTINGS = gql(/* GraphQL */ `
  mutation UpdateUserSettings($id: uuid!, $user: users_set_input!) {
    update_users_by_pk(pk_columns: { id: $id }, _set: $user) {
      id
      full_name
      ...UserSettingsFragment
    }
  }
`);

export const APPROVE_USER_ONBOARDING = gql(/* GraphQL */ `
  mutation ApproveUserOnboarding($id: uuid!) {
    update_users(
      where: { id: { _eq: $id }, onboarding_status: { _eq: pending_review } }
      _set: { onboarding_status: approved }
    ) {
      affected_rows
      returning {
        id
        onboarding_status
      }
    }
  }
`);

// For agents a CSM set up by hand: lets them into the CRM without the wizard.
// Guarded on in_progress, or no status yet for an agent who has the wizard
// ahead of them, so a submission that lands meanwhile goes to review instead
// of being approved unseen. The onboarding triggers log the status change and
// the completion with the CSM as actor; the reason lets the onboarding
// dashboard set aside agents who will never be published.
export const SKIP_USER_ONBOARDING = gql(/* GraphQL */ `
  mutation SkipUserOnboarding(
    $id: uuid!
    $reason: user_onboarding_skip_reason_enum_enum!
  ) {
    update_users(
      where: {
        id: { _eq: $id }
        _or: [
          { onboarding_status: { _eq: in_progress } }
          {
            onboarding_status: { _is_null: true }
            has_done_onboarding: { _eq: false }
          }
        ]
      }
      _set: {
        onboarding_status: approved
        has_done_onboarding: true
        onboarding_skip_reason: $reason
      }
    ) {
      affected_rows
      returning {
        id
        onboarding_status
      }
    }
  }
`);

export const GET_USER_ONBOARDING_HISTORY = gql(/* GraphQL */ `
  query GetUserOnboardingHistory($id: uuid!) {
    activities(
      where: {
        user_id: { _eq: $id }
        activity_type: {
          _in: [
            onboarding_enrolled
            onboarding_status_changed
            agent_profile_visibility_changed
          ]
        }
      }
      order_by: { created_at: asc }
    ) {
      id
      activity_type
      created_at
      created_by
      extra_data
      creator {
        id
        full_name
      }
    }
    users_by_pk(id: $id) {
      id
      onboarding_skip_reason
    }
  }
`);

export const UPDATE_USER_PERMISSION_FLAGS = gql(/* GraphQL */ `
  mutation UpdateUserPermissionFlags(
    $user_id: uuid!
    $is_admin: Boolean
    $can_edit_settings: Boolean
    $can_user_login_as: Boolean
  ) {
    update_user_permission_flags(
      user_id: $user_id
      is_admin: $is_admin
      can_edit_settings: $can_edit_settings
      can_user_login_as: $can_user_login_as
    ) {
      id
    }
  }
`);

export const GET_USERS_FOR_MERGE = gql(/* GraphQL */ `
  query GetUsersForMerge($ids: [uuid!]!) {
    users(where: { id: { _in: $ids } }) {
      id
      first_name
      last_name
      is_broker
      is_admin
      date_of_birth
      gender
      broker_id
      broker {
        id
        first_name
        last_name
      }
      emails(where: { primary: { _eq: true } }) {
        id
        email
      }
    }
  }
`);

export const MERGE_USER = gql(/* GraphQL */ `
  mutation MergeUser(
    $left_user_id: uuid!
    $right_user_id: uuid!
    $first_name: String
    $last_name: String
    $is_broker: Boolean
    $is_admin: Boolean
    $date_of_birth: date
    $gender: String
    $broker_id: uuid
    $primary_email: String
    $resolve_enquiries: Boolean
  ) {
    merge_user(
      left_user_id: $left_user_id
      right_user_id: $right_user_id
      first_name: $first_name
      last_name: $last_name
      is_broker: $is_broker
      is_admin: $is_admin
      date_of_birth: $date_of_birth
      gender: $gender
      broker_id: $broker_id
      primary_email: $primary_email
      resolve_enquiries: $resolve_enquiries
    ) {
      id
    }
  }
`);

export const DELETE_USER = gql(/* GraphQL */ `
  mutation DeleteUser($user_id: uuid!) {
    delete_user(user_id: $user_id) {
      id
    }
  }
`);

export const INSERT_USERS = gql(/* GraphQL */ `
  mutation InsertUsers($users: [users_insert_input!]!) {
    insert_users(
      objects: $users
      on_conflict: { constraint: users_pkey, update_columns: [] }
    ) {
      affected_rows
    }
  }
`);

export const IMPORT_USERS = gql(/* GraphQL */ `
  mutation ImportUsers($objects: [UserImportInput!]!) {
    import_users(objects: $objects) {
      label
      skipped
      skip_reason
    }
  }
`);

export const EXPORT_USERS = gql(/* GraphQL */ `
  query ExportUsers($where: users_bool_exp!) {
    users(where: $where, order_by: [{ created_at: desc }]) {
      id
      first_name
      last_name
      emails {
        email
      }
      phone_numbers {
        number
      }
      created_at
    }
  }
`);

export const INSERT_USER_EMAIL = gql(/* GraphQL */ `
  mutation InsertUserEmail($input: emails_insert_input!) {
    insert_emails_one(object: $input) {
      id
      email
      primary
      verified
      user_id
    }
  }
`);

export const UPDATE_USER_EMAIL = gql(/* GraphQL */ `
  mutation UpdateUserEmail($id: uuid!, $set: emails_set_input!) {
    update_emails_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      email
      primary
      verified
    }
  }
`);

export const DELETE_USER_EMAIL = gql(/* GraphQL */ `
  mutation DeleteUserEmail($id: uuid!) {
    delete_emails_by_pk(id: $id) {
      id
    }
  }
`);

export const UPDATE_PRIMARY_USER_EMAIL = gql(/* GraphQL */ `
  mutation UpdatePrimaryUserEmail($user_id: uuid!, $email_id: uuid!) {
    update_emails(
      where: { user_id: { _eq: $user_id } }
      _set: { primary: false }
    ) {
      affected_rows
    }
    update_emails_by_pk(
      pk_columns: { id: $email_id }
      _set: { primary: true }
    ) {
      id
      primary
    }
  }
`);

export const INSERT_USER_PHONE_NUMBER = gql(/* GraphQL */ `
  mutation InsertUserPhoneNumber($input: phone_numbers_insert_input!) {
    insert_phone_numbers_one(object: $input) {
      id
      number
      primary
      verified
      user_id
    }
  }
`);

export const UPDATE_USER_PHONE_NUMBER = gql(/* GraphQL */ `
  mutation UpdateUserPhoneNumber($id: uuid!, $set: phone_numbers_set_input!) {
    update_phone_numbers_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      number
      primary
      verified
    }
  }
`);

export const DELETE_USER_PHONE_NUMBER = gql(/* GraphQL */ `
  mutation DeleteUserPhoneNumber($id: uuid!) {
    delete_phone_numbers_by_pk(id: $id) {
      id
    }
  }
`);

export const GET_USER_PROFILE_COMPLETION = gql(/* GraphQL */ `
  query GetUserProfileCompletion($userId: uuid!, $since: timestamptz!) {
    users_by_pk(id: $userId) {
      id
      languages
      active_since
      description
      services
      user_images {
        image {
          id
          url
        }
      }
      transactions: property_transactions_aggregate(
        where: {
          signature_date: { _gte: $since, _lte: "now()" }
          is_published: { _eq: true }
          _not: { lot: { offer_type: { _eq: "rent" } } }
        }
      ) {
        aggregate {
          count
        }
      }
      reviews_aggregate(where: { completed: { _eq: true } }) {
        aggregate {
          count
        }
      }
    }
  }
`);

export const GET_USER_MODULE_GRANTS = gql(/* GraphQL */ `
  query GetUserModuleGrants($id: uuid!) {
    users_by_pk(id: $id) {
      id
      ...UserActiveModuleGrants
    }
  }
`);

export const GET_USER_MODULE_HISTORY = gql(/* GraphQL */ `
  query GetUserModuleHistory($id: uuid!) {
    users_by_pk(id: $id) {
      id
      activities(
        where: { activity_type: { _in: [module_granted, module_revoked] } }
        order_by: [{ done_at: desc_nulls_last }, { id: desc }]
      ) {
        id
        activity_type
        created_by
        done_at
        extra_data
      }
      module_grants {
        module_id
        granted_at
      }
    }
  }
`);

export const GET_MODULE_HISTORY_ACTORS = gql(/* GraphQL */ `
  query GetModuleHistoryActors($ids: [uuid!]!) {
    users(where: { id: { _in: $ids } }) {
      id
      full_name
      user_images(where: { is_primary: { _eq: true } }, limit: 1) {
        image {
          url
        }
      }
    }
  }
`);

export const SET_USER_MODULE_GRANT = gql(/* GraphQL */ `
  mutation SetUserModuleGrant(
    $id: uuid!
    $module: String!
    $enabled: Boolean!
  ) {
    set_user_module_grant(
      args: {
        target_user_id: $id
        target_module_id: $module
        target_enabled: $enabled
      }
    ) {
      id
      ...UserActiveModuleGrants
    }
  }
`);

export const UPDATE_USER_AUTO_ASSIGN_LEADS = gql(/* GraphQL */ `
  mutation UpdateUserAutoAssignLeads($id: uuid!, $autoAssignLeads: Boolean) {
    update_users_by_pk(
      pk_columns: { id: $id }
      _set: { auto_assign_leads: $autoAssignLeads }
    ) {
      id
      auto_assign_leads
    }
  }
`);

export const UPDATE_USER_NAME = gql(/* GraphQL */ `
  mutation UpdateUserName($id: uuid!, $first_name: String, $last_name: String) {
    update_users_by_pk(
      pk_columns: { id: $id }
      _set: { first_name: $first_name, last_name: $last_name }
    ) {
      id
      first_name
      last_name
      full_name
    }
  }
`);

export const UPDATE_USER_LANGUAGE = gql(/* GraphQL */ `
  mutation UpdateUserLanguage($id: uuid!, $language: user_language) {
    update_users_by_pk(pk_columns: { id: $id }, _set: { language: $language }) {
      id
      language
    }
  }
`);

export const SET_USER_HOME_ADDRESS = gql(/* GraphQL */ `
  mutation SetUserHomeAddress($id: uuid!, $user: users_set_input!) {
    update_users_by_pk(pk_columns: { id: $id }, _set: $user) {
      id
      home_address_route
      home_address_street_number
      home_address_postcode
      home_address_locality
      home_address_state
      home_address_country_code
      home_address_lat
      home_address_lng
    }
  }
`);

export const UPDATE_PRIMARY_USER_PHONE_NUMBER = gql(/* GraphQL */ `
  mutation UpdatePrimaryUserPhoneNumber($user_id: uuid!, $phone_id: uuid!) {
    update_phone_numbers(
      where: { user_id: { _eq: $user_id } }
      _set: { primary: false }
    ) {
      affected_rows
    }
    update_phone_numbers_by_pk(
      pk_columns: { id: $phone_id }
      _set: { primary: true }
    ) {
      id
      primary
    }
  }
`);

export const USER_FEED_V2 = gql(/* GraphQL */ `
  query UserFeedV2(
    $user_id: uuid!
    $after_date: timestamptz
    $overdue: Boolean
  ) {
    activities: get_activities_feed_for_user(
      args: {
        p_user_id: $user_id
        p_after_date: $after_date
        p_overdue: $overdue
      }
    ) {
      ...ActivityItem
      ...ActivityFiltersActivity
    }
  }
`);

export const GET_USER_DRAWER_TAB_COUNTS = gql(/* GraphQL */ `
  query GetUserDrawerTabCounts(
    $userId: uuid!
    $buyAppraisalReasons: [leads_appraisal_reason_enum!]!
  ) {
    selling_leads: leads_aggregate(
      where: {
        contact_id: { _eq: $userId }
        appraisal_reason: { _nin: $buyAppraisalReasons }
      }
    ) {
      aggregate {
        count
      }
    }
    listings: lots_aggregate(
      where: { sellers: { user_id: { _eq: $userId } } }
    ) {
      aggregate {
        count
      }
    }
    buyer_leads: buyer_leads_aggregate(where: { user_id: { _eq: $userId } }) {
      aggregate {
        count
      }
    }
    property_searches: property_searches_aggregate(
      where: { user_id: { _eq: $userId } }
    ) {
      aggregate {
        count
      }
    }
  }
`);
